import { fetchWithRetry } from 'shared/api/fetch-with-retry';
import { env } from 'shared/config';
import { FormValues, DataFromEsia, ErrorResponse, QuestionnaireResponse } from '../types';

const parseError = (e: unknown): ErrorResponse => {
  return {
    isError: true,
    status: typeof e === 'object' && e !== null && 'status' in e && typeof e.status === 'number' ? e.status : undefined,
    detail: (e as { detail?: string })?.detail || (e as { message?: string })?.message || 'Network error',
  };
};

const API_URL = `${env.API_URL?.replace(/\/$/, '')}/api/v1`;
const FETCH_RETRY_ATTEMPTS = 1;
const ESIA_FETCH_RETRY_ATTEMPTS = 20;
const ESIA_DATA_RETRIES_TIMEOUT = 5000;

export async function getQuestionnaire(token: string, businessUnit: string) {
  try {
    const response = await fetchWithRetry<QuestionnaireResponse>(
      `${API_URL}/questionnaires/${businessUnit}`,
      {
        method: 'GET',
        headers: {
          /* TODO: автоматически вставлять токен */
          Authorization: `Bearer ${token}`,
        },
      },
      FETCH_RETRY_ATTEMPTS,
    );

    return response;
  } catch (e) {
    return Promise.resolve(parseError(e));
  }
}

export async function redirectToEsia(token: string, businessUnit: string, sessionId: string) {
  try {
    const response = await fetchWithRetry<{ link: string }>(
      `${API_URL}/questionnaires/esia/redirect/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessUnit,
          sessionId,
        }),
      },
      FETCH_RETRY_ATTEMPTS,
    );

    return response;
  } catch (e) {
    return Promise.resolve(parseError(e));
  }
}

export async function loadDataFromEsia(token: string, businessUnit: string, code: string) {
  try {
    const config = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        businessUnit,
        code,
      }),
    };
    const getData = async (attempt = 0): Promise<DataFromEsia | Record<string, never>> => {
      let personalData: DataFromEsia | null = null;
      try {
        personalData = await fetchWithRetry<DataFromEsia>(
          `${API_URL}/questionnaires/esia/personal-data/`,
          config,
          FETCH_RETRY_ATTEMPTS,
        );
      } catch (e) {

      }

      if (personalData) {
        return personalData;
      }

      if (attempt < ESIA_FETCH_RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, ESIA_DATA_RETRIES_TIMEOUT));
        return getData(attempt + 1);
      }

      return {};
    };

    await fetchWithRetry<unknown>(
      `${API_URL}/questionnaires/esia/request/`,
      config,
      ESIA_FETCH_RETRY_ATTEMPTS,
    );

    const data = await getData();

    return data;
  } catch (e) {
    return Promise.resolve(parseError(e));
  }
}

export async function submitQuestionnaire(
  token: string,
  businessUnit: string,
  value: FormValues,
): Promise<ErrorResponse | { detail: string }> {
  try {
    await fetchWithRetry(
      `${API_URL}/personal-data-consent/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          isGranted: true,
          agreementType: 'basic',
          businessUnit,
          phoneMobile: value.phoneMobile.replace(/\s/g, ''),
          firstName: value.firstName,
          lastName: value.lastName,
          middleName: value.middleNameField.doesntExist ? null : value.middleNameField.middleName,
        }),
      },
      FETCH_RETRY_ATTEMPTS,
    );

    const response = await fetchWithRetry<{ detail: string }>(
      `${API_URL}/questionnaires/${businessUnit}/`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          lastName: value.lastName,
          firstName: value.firstName,
          middleName: value.middleNameField.doesntExist ? null : value.middleNameField.middleName,
          middleNameDoesntExist: value.middleNameField.doesntExist,
          birthDate: value.birthDate,
          birthPlace: value.birthPlace,
          documentType: 'Passport',
          documentSerial: value.documentSerial.replace(/\s/g, ''),
          documentNumber: value.documentNumber.replace(/\s/g, ''),
          documentIssuedBy: value.documentIssuedBy,
          documentIssueDate: value.documentIssueDate,
          addressReg: value.addressReg,
          addressFact: value.addressFactField.same ? value.addressReg : value.addressFactField.addressFact,
          INN: value.INN.replace(/\s/g, '') || null,
          phoneMobile: value.phoneMobile.replace(/\s/g, '') || null,
          isGosuslugi: value.isGosuslugi,
        }),
      },
      FETCH_RETRY_ATTEMPTS,
    );

    return response;
  } catch (e) {
    return Promise.resolve(parseError(e));
  }
}
